import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  Key,
  UserPlus,
  FileCheck,
  Plus,
  Trash2,
  GripVertical,
  Loader2,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { Button } from '../components/ui/Button.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AuthMethodsConfig {
  emailPassword: { enabled: boolean };
  google: { enabled: boolean };
  meta: { enabled: boolean };
  phoneOtp: { enabled: boolean };
  magicLink: { enabled: boolean };
}

interface SpaceAuthConfigResponse {
  effective: { methods: AuthMethodsConfig };
  spaceOverride: { methods?: Partial<AuthMethodsConfig> };
  signupFields: SignupFieldConfig[];
  consentCheckboxes: ConsentCheckbox[];
}

interface SignupFieldConfig {
  field: string;
  label: string;
  required: boolean;
  type: 'text' | 'date' | 'select' | 'phone' | 'email';
  options?: string[];
  placeholder?: string;
}

interface ConsentCheckbox {
  id: string;
  label: string;
  linkUrl?: string;
  linkText?: string;
  required: boolean;
  consentChannel?: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const AUTH_METHODS = [
  { key: 'emailPassword' as const, label: 'Email + Password', description: 'Standard email and password login' },
  { key: 'google' as const, label: 'Google SSO', description: 'Sign in with Google account' },
  { key: 'meta' as const, label: 'Meta (Facebook) SSO', description: 'Sign in with Facebook account' },
  { key: 'phoneOtp' as const, label: 'Phone OTP', description: 'One-time code sent via SMS' },
  { key: 'magicLink' as const, label: 'Magic Link', description: 'Passwordless login via email link' },
];

const SIGNUP_FIELD_OPTIONS = [
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'dateOfBirth', label: 'Date of Birth' },
  { value: 'gender', label: 'Gender' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
  { value: 'custom', label: 'Custom...' },
];

const FIELD_TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
];

// ─── Shared styles ──────────────────────────────────────────────────────────

const inputCls =
  'w-full border border-zinc-200 rounded-lg text-sm px-3 h-9 focus:outline-none focus:ring-2 focus:ring-zinc-800 bg-white';
const labelCls = 'block text-xs font-medium text-zinc-500 mb-1.5';

// ─── Section wrapper ────────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  description,
  children,
  footer,
}: {
  icon: React.ElementType;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-zinc-100">
        <Icon size={15} className="text-zinc-500" />
        <div>
          <h2 className="text-sm font-semibold text-zinc-800">{title}</h2>
          {description && <p className="text-xs text-zinc-400 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="p-5">{children}</div>
      {footer && (
        <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 flex items-center justify-between">
          {footer}
        </div>
      )}
    </div>
  );
}

// ─── Toggle row ─────────────────────────────────────────────────────────────

function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:opacity-40 ${
        checked ? 'bg-zinc-900' : 'bg-zinc-200'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-4' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

// ─── Auth Method Override Row ────────────────────────────────────────────────

function AuthMethodRow({
  label,
  description,
  useTenantDefault,
  effectiveEnabled,
  overrideEnabled,
  onToggleDefault,
  onToggleEnabled,
}: {
  label: string;
  description: string;
  useTenantDefault: boolean;
  effectiveEnabled: boolean;
  overrideEnabled: boolean;
  onToggleDefault: (v: boolean) => void;
  onToggleEnabled: (v: boolean) => void;
}) {
  return (
    <div className="py-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="min-w-0 mr-4">
          <span className="text-sm text-zinc-800 font-medium">{label}</span>
          <p className="text-xs text-zinc-400 mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={useTenantDefault}
              onChange={(e) => onToggleDefault(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-800"
            />
            <span className="text-xs text-zinc-500">Tenant default</span>
          </label>
          {!useTenantDefault && (
            <ToggleSwitch checked={overrideEnabled} onChange={onToggleEnabled} />
          )}
          {useTenantDefault && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${effectiveEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
              {effectiveEnabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function SpaceAuthSettingsTab({ spaceId }: { spaceId: string }) {
  const toast = useToast();
  const qc = useQueryClient();

  // ─── Load auth config ───────────────────────────────────────────────────
  const { data, isLoading, error } = useQuery<SpaceAuthConfigResponse>({
    queryKey: ['space-auth-config', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}/auth-config`).then((r) => r.data),
    enabled: !!spaceId,
  });

  // ─── Auth overrides state ───────────────────────────────────────────────
  type MethodKey = keyof AuthMethodsConfig;
  const [overrides, setOverrides] = useState<Record<MethodKey, { useDefault: boolean; enabled: boolean }>>({
    emailPassword: { useDefault: true, enabled: true },
    google: { useDefault: true, enabled: false },
    meta: { useDefault: true, enabled: false },
    phoneOtp: { useDefault: true, enabled: false },
    magicLink: { useDefault: true, enabled: false },
  });
  const [overridesDirty, setOverridesDirty] = useState(false);

  // ─── Signup fields state ────────────────────────────────────────────────
  const [signupFields, setSignupFields] = useState<SignupFieldConfig[]>([]);
  const [fieldsDirty, setFieldsDirty] = useState(false);

  // ─── Consent checkboxes state ───────────────────────────────────────────
  const [consentCheckboxes, setConsentCheckboxes] = useState<ConsentCheckbox[]>([]);
  const [consentDirty, setConsentDirty] = useState(false);

  // ─── Sync server data → local state ─────────────────────────────────────
  useEffect(() => {
    if (!data) return;
    const so = data.spaceOverride?.methods ?? {};
    const eff = data.effective?.methods ?? {};

    const newOverrides = {} as typeof overrides;
    for (const m of AUTH_METHODS) {
      const hasOverride = so[m.key] !== undefined;
      newOverrides[m.key] = {
        useDefault: !hasOverride,
        enabled: hasOverride ? (so[m.key]?.enabled ?? false) : (eff[m.key]?.enabled ?? false),
      };
    }
    setOverrides(newOverrides);
    setOverridesDirty(false);

    setSignupFields(data.signupFields ?? []);
    setFieldsDirty(false);

    setConsentCheckboxes(data.consentCheckboxes ?? []);
    setConsentDirty(false);
  }, [data]);

  // ─── Mutations ──────────────────────────────────────────────────────────

  const saveOverridesMut = useMutation({
    mutationFn: () => {
      const methods: Record<string, { enabled: boolean }> = {};
      for (const m of AUTH_METHODS) {
        if (!overrides[m.key].useDefault) {
          methods[m.key] = { enabled: overrides[m.key].enabled };
        }
      }
      // If all are default, send empty methods (clears override)
      return api.patch(`/api/v1/cms/spaces/${spaceId}/auth-config`, {
        methods: Object.keys(methods).length > 0 ? methods : undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space-auth-config', spaceId] });
      toast.success('Auth method overrides saved');
      setOverridesDirty(false);
    },
    onError: (err) => toast.error(extractError(err, 'Error saving overrides')),
  });

  const saveFieldsMut = useMutation({
    mutationFn: () => api.patch(`/api/v1/cms/spaces/${spaceId}/signup-fields`, signupFields),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space-auth-config', spaceId] });
      toast.success('Signup fields saved');
      setFieldsDirty(false);
    },
    onError: (err) => toast.error(extractError(err, 'Error saving signup fields')),
  });

  const saveConsentMut = useMutation({
    mutationFn: () => api.patch(`/api/v1/cms/spaces/${spaceId}/consent-checkboxes`, consentCheckboxes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space-auth-config', spaceId] });
      toast.success('Consent checkboxes saved');
      setConsentDirty(false);
    },
    onError: (err) => toast.error(extractError(err, 'Error saving consent checkboxes')),
  });

  // ─── Helpers ────────────────────────────────────────────────────────────

  function addSignupField() {
    setSignupFields((prev) => [
      ...prev,
      { field: '', label: '', required: false, type: 'text' },
    ]);
    setFieldsDirty(true);
  }

  function updateField(index: number, patch: Partial<SignupFieldConfig>) {
    setSignupFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
    setFieldsDirty(true);
  }

  function removeField(index: number) {
    setSignupFields((prev) => prev.filter((_, i) => i !== index));
    setFieldsDirty(true);
  }

  function addConsentCheckbox() {
    setConsentCheckboxes((prev) => [
      ...prev,
      { id: '', label: '', required: false },
    ]);
    setConsentDirty(true);
  }

  function updateConsent(index: number, patch: Partial<ConsentCheckbox>) {
    setConsentCheckboxes((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
    setConsentDirty(true);
  }

  function removeConsent(index: number) {
    setConsentCheckboxes((prev) => prev.filter((_, i) => i !== index));
    setConsentDirty(true);
  }

  function slugify(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '')
      .slice(0, 64);
  }

  // ─── Loading / Error ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-red-500">
        <AlertCircle size={16} className="mr-2" />
        Failed to load auth config
      </div>
    );
  }

  const effectiveMethods = data?.effective?.methods ?? {};

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-3xl">
      {/* ─── A: Auth Method Overrides ─── */}
      <Section
        icon={Key}
        title="Auth Method Overrides"
        description="Override tenant-level auth methods for this space. Uncheck 'Tenant default' to customize."
        footer={
          <>
            <div className="text-xs text-zinc-500">
              {overridesDirty ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <AlertCircle size={12} />
                  Unsaved changes
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-zinc-400">
                  <CheckCircle size={12} />
                  Saved
                </span>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => saveOverridesMut.mutate()}
              disabled={!overridesDirty || saveOverridesMut.isPending}
              loading={saveOverridesMut.isPending}
            >
              <Save size={13} className="mr-1.5" />
              Save Overrides
            </Button>
          </>
        }
      >
        <div className="divide-y divide-zinc-100">
          {AUTH_METHODS.map((m) => (
            <AuthMethodRow
              key={m.key}
              label={m.label}
              description={m.description}
              useTenantDefault={overrides[m.key].useDefault}
              effectiveEnabled={(effectiveMethods as AuthMethodsConfig)[m.key]?.enabled ?? false}
              overrideEnabled={overrides[m.key].enabled}
              onToggleDefault={(useDefault) => {
                setOverrides((prev) => ({
                  ...prev,
                  [m.key]: {
                    ...prev[m.key],
                    useDefault,
                    enabled: useDefault
                      ? (effectiveMethods as AuthMethodsConfig)[m.key]?.enabled ?? false
                      : prev[m.key].enabled,
                  },
                }));
                setOverridesDirty(true);
              }}
              onToggleEnabled={(enabled) => {
                setOverrides((prev) => ({
                  ...prev,
                  [m.key]: { ...prev[m.key], enabled },
                }));
                setOverridesDirty(true);
              }}
            />
          ))}
        </div>
      </Section>

      {/* ─── B: Signup Fields Builder ─── */}
      <Section
        icon={UserPlus}
        title="Signup Fields"
        description="Configure additional fields shown on the signup form for this space."
        footer={
          <>
            <div className="text-xs text-zinc-500">
              {fieldsDirty ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <AlertCircle size={12} />
                  Unsaved changes
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-zinc-400">
                  <CheckCircle size={12} />
                  Saved
                </span>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => saveFieldsMut.mutate()}
              disabled={!fieldsDirty || saveFieldsMut.isPending}
              loading={saveFieldsMut.isPending}
            >
              <Save size={13} className="mr-1.5" />
              Save Fields
            </Button>
          </>
        }
      >
        {signupFields.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">No signup fields configured. Using defaults (first name, last name).</p>
        ) : (
          <div className="space-y-3">
            {signupFields.map((field, idx) => (
              <div key={idx} className="flex items-start gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                <GripVertical size={14} className="text-zinc-300 mt-2 shrink-0" />
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Field name */}
                  <div>
                    <label className={labelCls}>Field</label>
                    <select
                      value={SIGNUP_FIELD_OPTIONS.some((o) => o.value === field.field) ? field.field : 'custom'}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === 'custom') {
                          updateField(idx, { field: '' });
                        } else {
                          const opt = SIGNUP_FIELD_OPTIONS.find((o) => o.value === v);
                          updateField(idx, {
                            field: v,
                            label: field.label || opt?.label || v,
                          });
                        }
                      }}
                      className={inputCls}
                    >
                      <option value="">-- Select --</option>
                      {SIGNUP_FIELD_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    {(!SIGNUP_FIELD_OPTIONS.some((o) => o.value === field.field) || field.field === '') && (
                      <input
                        type="text"
                        value={field.field}
                        onChange={(e) => updateField(idx, { field: e.target.value })}
                        placeholder="Custom field name"
                        className={`${inputCls} mt-1.5`}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <div>
                    <label className={labelCls}>Label</label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(idx, { label: e.target.value })}
                      placeholder="Display label"
                      className={inputCls}
                    />
                  </div>

                  {/* Type */}
                  <div>
                    <label className={labelCls}>Type</label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(idx, { type: e.target.value as SignupFieldConfig['type'] })}
                      className={inputCls}
                    >
                      {FIELD_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Required */}
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateField(idx, { required: e.target.checked })}
                        className="h-3.5 w-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-800"
                      />
                      <span className="text-sm text-zinc-700">Required</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={() => removeField(idx)}
                  className="p-1.5 text-zinc-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors shrink-0 mt-1"
                  title="Remove field"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3">
          <Button variant="ghost" size="sm" onClick={addSignupField}>
            <Plus size={13} className="mr-1.5" />
            Add Field
          </Button>
        </div>
      </Section>

      {/* ─── C: Consent Checkboxes Builder ─── */}
      <Section
        icon={FileCheck}
        title="Consent Checkboxes"
        description="Add consent checkboxes shown during signup (e.g. terms of service, marketing opt-in)."
        footer={
          <>
            <div className="text-xs text-zinc-500">
              {consentDirty ? (
                <span className="flex items-center gap-1.5 text-amber-600">
                  <AlertCircle size={12} />
                  Unsaved changes
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-zinc-400">
                  <CheckCircle size={12} />
                  Saved
                </span>
              )}
            </div>
            <Button
              size="sm"
              onClick={() => saveConsentMut.mutate()}
              disabled={!consentDirty || saveConsentMut.isPending}
              loading={saveConsentMut.isPending}
            >
              <Save size={13} className="mr-1.5" />
              Save Checkboxes
            </Button>
          </>
        }
      >
        {consentCheckboxes.length === 0 ? (
          <p className="text-sm text-zinc-400 italic">No consent checkboxes configured.</p>
        ) : (
          <div className="space-y-3">
            {consentCheckboxes.map((cb, idx) => (
              <div key={idx} className="flex items-start gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
                <GripVertical size={14} className="text-zinc-300 mt-2 shrink-0" />
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* ID */}
                  <div>
                    <label className={labelCls}>ID (slug)</label>
                    <input
                      type="text"
                      value={cb.id}
                      onChange={(e) => updateConsent(idx, { id: e.target.value })}
                      onBlur={() => {
                        if (!cb.id && cb.label) {
                          updateConsent(idx, { id: slugify(cb.label) });
                        }
                      }}
                      placeholder="e.g. terms_accepted"
                      className={`${inputCls} font-mono`}
                    />
                  </div>

                  {/* Label */}
                  <div>
                    <label className={labelCls}>Label text</label>
                    <input
                      type="text"
                      value={cb.label}
                      onChange={(e) => {
                        const newLabel = e.target.value;
                        const patch: Partial<ConsentCheckbox> = { label: newLabel };
                        if (!cb.id) patch.id = slugify(newLabel);
                        updateConsent(idx, patch);
                      }}
                      placeholder="I accept the Terms of Service"
                      className={inputCls}
                    />
                  </div>

                  {/* Link URL */}
                  <div>
                    <label className={labelCls}>Link URL (optional)</label>
                    <input
                      type="url"
                      value={cb.linkUrl ?? ''}
                      onChange={(e) => updateConsent(idx, { linkUrl: e.target.value || undefined })}
                      placeholder="https://example.com/terms"
                      className={inputCls}
                    />
                  </div>

                  {/* Link Text */}
                  <div>
                    <label className={labelCls}>Link text (optional)</label>
                    <input
                      type="text"
                      value={cb.linkText ?? ''}
                      onChange={(e) => updateConsent(idx, { linkText: e.target.value || undefined })}
                      placeholder="Terms of Service"
                      className={inputCls}
                    />
                  </div>

                  {/* Consent Channel */}
                  <div>
                    <label className={labelCls}>Consent channel (optional)</label>
                    <input
                      type="text"
                      value={cb.consentChannel ?? ''}
                      onChange={(e) => updateConsent(idx, { consentChannel: e.target.value || undefined })}
                      placeholder="e.g. marketing_email"
                      className={inputCls}
                    />
                  </div>

                  {/* Required */}
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cb.required}
                        onChange={(e) => updateConsent(idx, { required: e.target.checked })}
                        className="h-3.5 w-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-800"
                      />
                      <span className="text-sm text-zinc-700">Required</span>
                    </label>
                  </div>
                </div>
                <button
                  onClick={() => removeConsent(idx)}
                  className="p-1.5 text-zinc-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors shrink-0 mt-1"
                  title="Remove checkbox"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3">
          <Button variant="ghost" size="sm" onClick={addConsentCheckbox}>
            <Plus size={13} className="mr-1.5" />
            Add Checkbox
          </Button>
        </div>
      </Section>
    </div>
  );
}
